import { Col, Form, Row } from "react-bootstrap";
import { useFormContext, UseFieldArrayRemove } from "react-hook-form";
import { SingleValue } from "react-select";
import { useContext, useEffect, useState } from "react";
import { TipoImposto } from "constants/EnumTipoImposto";
import { useGetProximoChequeDisponivel } from "hooks/groupPay/useGetProximoChequeDisponivel";
import { UserContext } from "context/UserContext";
import { api } from "constants/RotasAPI";
import { getOpcoesPagamento } from "./SecaoDadosDespesaModalCadastrarDespesaUnicaGroupPay";
import { CadastrarDespesaUnicaGroupPayForm } from "types/reactHookForm/formTypes/CadastrarDespesaUnicaGroupPayForm";
import { RetencoesYupSchemaDTO } from "types/dto/imposto/RetencoesYupSchemaDTO";
import { RegistroImpostoSelectAsyncDTO } from "types/dto/imposto/RegistroImpostoSelectAsyncDTO";
import { SelectOptionType } from "types/select/SelectOptionType";
import showAlert from "shared/util/functions/alert/showAlert";
import {
  criarDataByLocalDate,
  formatarValorMonetarioInputNumerico,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";

import CirculoSinalNegativo from "shared/components/circuloSinalNegativo/CirculoSinalNegatvo";
import CustomNumberInput from "shared/components/input/CustomNumberInput";
import TextInput from "shared/components/input/TextInput";
import CustomSelect from "shared/components/select/CustomSelect";
import SelectAsync from "shared/components/select/SelectAsync";
import CustomSwitch from "shared/components/Switch/CustomSwitch";
import InputAnexo from "shared/components/input/InputAnexo";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { MesCompetencia } from "shared/components/date/MesCompetencia";

interface LinhaRetencaoProps {
  index: number;
  removerRetencao: UseFieldArrayRemove;
  condominioId: number;
  isParcelada?: boolean;
}

const LinhaRetencao: React.FC<LinhaRetencaoProps> = ({ index, removerRetencao, condominioId, isParcelada }) => {
  const { register, control, setValue, watch } = useFormContext<CadastrarDespesaUnicaGroupPayForm>();
  const [uiState, setUiState] = useState<{ manualBarcode: boolean; showAttachment: boolean }>({
    manualBarcode: false,
    showAttachment: false,
  });

  const conta = watch(`retencoes.${index}.conta`);
  const formaPagamento = watch(`retencoes.${index}.formaPagamento`);
  const formaPagamentoPix = watch(`retencoes.${index}.tipoFormaPagamentoPix`);
  const { competenciaSelecionada } = useContext(UserContext);

  const dataVencimentoValue = watch(`retencoes.${index}.dataVencimento`);
  const mesCompetenciaValue = watch(`retencoes.${index}.mesCompetencia`);

  const { data: proximoCheque } = useGetProximoChequeDisponivel(conta?.value?.contaBancaria?.id);

  useEffect(() => {
    if (proximoCheque?.numero) {
      setValue(`retencoes.${index}.numeroCheque`, proximoCheque.numero.toString());
    } else {
      setValue(`retencoes.${index}.numeroCheque`, "");
    }
  }, [proximoCheque, index, setValue]);

  const handleUiStateChange = (key: "manualBarcode" | "showAttachment", value: boolean) => {
    setUiState((prev) => ({ ...prev, [key]: value }));
  };

  const setarValorRetencao = (valorAliquota: number) => {
    let valorBaseInput = watch(`retencoes.${index}.valorBase`);

    const valorBase =
      typeof valorBaseInput === "number" ? valorBaseInput : formatarValorMonetarioInputNumerico(valorBaseInput || "0");
    const valorRetencao = valorBase * (valorAliquota / 100);

    setValue(`retencoes.${index}.valor`, valorRetencao, { shouldValidate: true });

    verificarAlertaDeImposto();
  };

  const getValorNumericoRetencao = (retencao: RetencoesYupSchemaDTO): number => {
    const valorRetencaoInput = retencao.valor;
    if (!valorRetencaoInput) return 0;
    return typeof valorRetencaoInput === "number"
      ? valorRetencaoInput
      : formatarValorMonetarioInputNumerico(valorRetencaoInput);
  };

  const isValorRetencaoMenorQueDezReais = (): boolean => {
    const retencao = watch(`retencoes.${index}`);
    return getValorNumericoRetencao(retencao) < 10;
  };

  const verificarAlertaDeImposto = () => {
    const impostoSelecionado = watch(`retencoes.${index}.imposto`);
    const tipoImposto = impostoSelecionado?.value?.tipoImposto;

    const deveExibirAlerta =
      tipoImposto && [TipoImposto.DARF, TipoImposto.GPS].includes(tipoImposto) && isValorRetencaoMenorQueDezReais();

    if (deveExibirAlerta) {
      exibirAlertaDeImposto(tipoImposto);
    }
  };

  const exibirAlertaDeImposto = (tipoImposto: TipoImposto) => {
    if (tipoImposto === TipoImposto.DARF) {
      showAlert({
        tipoAlerta: "OK_ALERT",
        texto:
          "Conforme instrução normativa SRF Nº82 de 27/12/1996: É vedado o recolhimento de tributos e contribuições administrados pela Secretaria da Receita Federal cujo valor total seja inferior a R$ 10,00.",
      });
    } else if (tipoImposto === TipoImposto.GPS) {
      showAlert({
        tipoAlerta: "OK_ALERT",
        texto:
          "Conforme instrução normativa RFN 1.238 de 11/01/2012: vedado o recolhimento, em documento de arrecadação, de valor inferior a R$ 10,00 (dez reais).",
      });
    }
  };

  const handleFormaPagamento = () => {
    return getOpcoesPagamento(conta?.value);
  };

  return (
    <div className="border-bottom mb-3 pb-3">
      {isParcelada && index === 0 && (
        <div className="m-t-lg m-l mb-4">
          <CustomSwitch
            name={"reterEmTodasParcelas"}
            labelDireito="Reter imposto em todas as parcelas"
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
          />
        </div>
      )}
      <Row className="mt-3 align-items-end">
        <Col xs={3}>
          <CustomNumberInput
            id={`valorBase-${index}`}
            name={`retencoes.${index}.valorBase`}
            label="Valor base (R$)"
            prefix="R$ "
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            decimalScale={2}
            fixedDecimalScale={true}
            placeholder="R$ 0,00"
            isAllowed={(values) => {
              const { floatValue } = values;
              return void 0 === floatValue || (floatValue > 0 && floatValue <= 999999999999.99);
            }}
            onBlur={() => {
              if (watch(`retencoes.${index}.imposto`)?.value?.aliquota) {
                setarValorRetencao(watch(`retencoes.${index}.imposto`)?.value?.aliquota);
              }
            }}
          />
        </Col>
        <Col xs={2}>
          <SelectAsync
            id={`${index}.impostoId`}
            name={`retencoes.${index}.imposto`}
            label="Imposto *"
            endpoint={`api/imposto/buscar-registros-select-async?condominioId=${condominioId}`}
            nomeAtributoLabel="descricaoImposto"
            nomeAtributoValue="value"
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            isClearable={true}
            onChange={(opt: SingleValue<SelectOptionType<RegistroImpostoSelectAsyncDTO>>) => {
              setarValorRetencao(opt?.value?.aliquota ?? 0);
            }}
          />
        </Col>
        <Col xs={3}>
          <CustomDatePicker
            name={`retencoes.${index}.dataVencimento`}
            label="Data de vencimento *"
            value={dataVencimentoValue ? new Date(dataVencimentoValue) : undefined}
            usarValidacaoParaFormulario
            popperDirection="top"
            control={control}
            register={register}
            setValue={setValue}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={3}>
          <CustomNumberInput
            id={`valor-${index}`}
            name={`retencoes.${index}.valor`}
            label="Valor (R$) *"
            prefix="R$ "
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            decimalScale={2}
            fixedDecimalScale={true}
            placeholder="R$ 0,00"
            isAllowed={(values) => {
              const { floatValue } = values;
              return void 0 === floatValue || (floatValue > 0 && floatValue <= 999999999999.99);
            }}
          />
        </Col>
        {index > 0 && (
          <Col xs={1} className="pb-1">
            <CirculoSinalNegativo onClick={() => removerRetencao(index)} size={16} />
          </Col>
        )}
      </Row>

      <Row className="mt-3">
        <Col xs={3}>
          <MesCompetencia
            name={`retencoes.${index}.mesCompetencia`}
            label="Mês competência *"
            value={mesCompetenciaValue}
            usarValidacaoParaFormulario
            popperDirection="top"
            control={control}
            register={register}
            setValue={setValue}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={2}>
          <CustomSelect
            id={`contribuinte-${index}`}
            label="Contribuinte"
            name={`retencoes.${index}.contribuinte`}
            options={[
              { label: "Condomínio", value: "CONDOMINIO" },
              { label: "Fornecedor", value: "FORNECEDOR" },
            ]}
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            isClearable={true}
          />
        </Col>
        <Col xs={3}>
          <SelectAsync
            id={`conta-${index}`}
            label="Conta (bancária, caixinha...)"
            name={`retencoes.${index}.conta`}
            endpoint={`${api.conta.getContasByCondominio}/${condominioId}?ativo=true`}
            nomeAtributoValue="conta"
            nomeAtributoLabel="conta.descricao"
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            isClearable={true}
            onChange={() => {
              setValue(`retencoes.${index}.formaPagamento`, null, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue(`retencoes.${index}.tipoFormaPagamentoPix`, null, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        </Col>
        <Col xs={3}>
          <CustomSelect
            id={`formaPagamento-${index}`}
            label="Forma de pagamento"
            name={`retencoes.${index}.formaPagamento`}
            options={handleFormaPagamento()}
            usarValidacaoParaFormulario
            control={control}
            register={register}
            setValue={setValue}
            isClearable={true}
            onChange={(opcao) => {
              if (opcao?.value === "PIX") {
                setValue(`retencoes.${index}.tipoFormaPagamentoPix`, "TRANSFERENCIA");
              } else {
                setValue(`retencoes.${index}.tipoFormaPagamentoPix`, null);
              }
            }}
          />
        </Col>
      </Row>

      {formaPagamento?.value === "CHEQUE" && (
        <Row className="mt-3">
          <Col xs={3}>
            <TextInput
              usarValidacaoParaFormulario={true}
              id={`numeroCheque-${index}`}
              label="Número do cheque"
              name={`retencoes.${index}.numeroCheque`}
              control={control}
              setValue={setValue}
              register={register}
            />
          </Col>
        </Row>
      )}

      {formaPagamento?.value === "PIX" && (
        <Row className="mt-3">
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Forma de Pagamento PIX</Form.Label>
              <div className="d-flex pt-2">
                <Form.Check
                  inline
                  label="Transferência"
                  type="radio"
                  value="TRANSFERENCIA"
                  {...register(`retencoes.${index}.tipoFormaPagamentoPix`)}
                />
                <Form.Check
                  inline
                  label="QR Code"
                  type="radio"
                  value="QR_CODE"
                  {...register(`retencoes.${index}.tipoFormaPagamentoPix`)}
                />
              </div>
            </Form.Group>
          </Col>
          {formaPagamentoPix === "QR_CODE" && (
            <Col xs={7}>
              <TextInput
                usarValidacaoParaFormulario={true}
                id={`codigoCopiaColaPix-${index}`}
                name={`retencoes.${index}.codigoCopiaColaPix`}
                label="Código Copia e Cola PIX"
                control={control}
                setValue={setValue}
                register={register}
              />
            </Col>
          )}
        </Row>
      )}

      <Row className="mt-3 align-items-end">
        <Col xs={8}>
          <TextInput
            id={`codigoDeBarras-${index}`}
            label={uiState.manualBarcode ? "Linha digitável" : "Código de barras"}
            name={`retencoes.${index}.codigoDeBarras`}
            placeholder={
              uiState.manualBarcode ? "Informe a linha digitável" : "Aguardando a leitura do código de barras"
            }
            usarValidacaoParaFormulario
            readOnly={!uiState.manualBarcode}
            control={control}
            register={register}
            setValue={setValue}
          />
        </Col>
        <Col xs={4} className="pb-1">
          <Form.Check
            type="checkbox"
            label="Informar manualmente"
            checked={uiState.manualBarcode}
            onChange={(e) => handleUiStateChange("manualBarcode", e.target.checked)}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <CustomSwitch
          labelDireito="Anexo"
          usarValidacaoParaFormulario={false}
          checked={uiState.showAttachment}
          onChange={(e) => handleUiStateChange("showAttachment", e.target.checked)}
        />
        {uiState.showAttachment && (
          <Col xs={4}>
            <InputAnexo
              id={`retencoes-anexo-${index}`}
              name={`retencoes.${index}.anexo`}
              accept=".pdf, .png, .jpg, .jpeg"
              control={control}
              setValue={setValue}
              hasInfoAdicional={true}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default LinhaRetencao;
